const origin = String(import.meta.env?.VITE_DORORAID_DATA_API_URL || 'https://dororaid-data.kukla1255.workers.dev').replace(/\/+$/, '');
const cache = new Map();

// thumb 은 무거운 GIF 의 첫 프레임 썸네일이다(stickerService.js). 여기서 빠지면 주소가 버려지고
// 고르개가 1.9MB 원본을 그대로 받는다 - #465 를 배포하고 실제로 그랬다.
export function stickerAssetUrl(path) {
  return typeof path === 'string' && /^\/v1\/stickers\/[^/]+\/(media|poster|thumb)$/.test(path) ? `${origin}${path}` : '';
}

export function invalidateStickerCache(path = '') {
  if (!path) {
    cache.clear();
    return;
  }
  cache.delete(path);
  // 같은 목록을 scope 별로 따로 담는다. '/v1/sticker-packs' 로 비우면 그 변형들도 같이 비워야
  // 운영진이 콘팩을 고친 뒤 고르개가 옛 목록을 5분간 더 보여 주지 않는다.
  const prefix = `${path}?`;
  for (const [key, entry] of [...cache.entries()]) {
    if (key.startsWith(prefix) || entry.sourcePath === path || entry.sourcePath?.startsWith(prefix)) cache.delete(key);
  }
}

export function readCachedSticker(stickerId, scope = 'control') {
  const path = `/v1/stickers/${encodeURIComponent(stickerId)}?scope=${encodeURIComponent(scope)}`;
  const entry = cache.get(path);
  return entry?.expires > Date.now() ? entry.value?.sticker || null : null;
}

export function rememberStickerForChat(sticker, scope = 'control', ttlMs = 60000) {
  if (!sticker?.id) return;
  const key = `/v1/stickers/${encodeURIComponent(sticker.id)}?scope=${encodeURIComponent(scope)}`;
  const value = { ok: true, sticker };
  const sourcePath = sticker.packId ? `/v1/sticker-packs/${encodeURIComponent(sticker.packId)}?scope=${encodeURIComponent(scope)}` : undefined;
  cache.set(key, { promise: Promise.resolve(value), value, expires: Date.now() + ttlMs, sourcePath });
  while (cache.size > 600) cache.delete(cache.keys().next().value);
}

function seedPackStickers(path, result, expires) {
  if (!/^\/v1\/sticker-packs\/[^/?]+\?/.test(path) || !Array.isArray(result?.stickers)) return;
  const scope = new URLSearchParams(path.split('?')[1]).get('scope') || 'control';
  for (const sticker of result.stickers) {
    if (!sticker?.id) continue;
    rememberStickerForChat(sticker, scope, Math.max(0, expires - Date.now()));
    const key = `/v1/stickers/${encodeURIComponent(sticker.id)}?scope=${encodeURIComponent(scope)}`;
    const entry = cache.get(key);
    if (entry) entry.sourcePath = path;
  }
  while (cache.size > 600) cache.delete(cache.keys().next().value);
}

export async function loadStickers(path, { force = false, ttlMs = 60000 } = {}) {
  const cached = force ? null : cache.get(path);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = fetch(`${origin}${path}`, {
    cache: force ? 'no-store' : 'default',
  }).then(async (response) => {
    if (!response.ok) {
      const error = new Error('콘을 불러오지 못했습니다.');
      error.status = response.status;
      throw error;
    }
    return response.json();
  });
  if (ttlMs > 0) {
    cache.set(path, { promise, expires: Date.now() + ttlMs });
    if (cache.size > 600) cache.delete(cache.keys().next().value);
  } else {
    cache.delete(path);
  }
  try {
    const result = await promise;
    const entry = cache.get(path);
    if (entry?.promise === promise) {
      entry.value = result;
      seedPackStickers(path, result, entry.expires);
    }
    return result;
  } catch (error) {
    if (cache.get(path)?.promise === promise) cache.delete(path);
    throw error;
  }
}


// 즐겨찾기는 이 브라우저에만 둔다. 서버에 두면 로그인한 사람만 쓸 수 있고(중계방 게스트는 401),
// 고르개를 열 때마다 왕복이 두 번 더 붙는다. 기기마다 달라지는 대신 바로 뜨고 누구나 쓴다.
const FAVORITES_KEY = 'doroparty:sticker-favorites';
const FAVORITES_LIMIT = 120;

export function readFavoriteIds() {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? [...new Set(list.filter((id) => typeof id === 'string' && id))].slice(0, FAVORITES_LIMIT) : [];
  } catch {
    // 사생활 보호 모드이거나 저장된 값이 깨졌다. 즐겨찾기가 비어 보일 뿐 고르개는 그대로 쓴다.
    return [];
  }
}

function writeFavoriteIds(ids) {
  // 실패를 호출자에게 전달해야 별 표시를 되돌리고 저장 오류를 알릴 수 있다.
  window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids.slice(0, FAVORITES_LIMIT)));
}

// 컴포넌트가 기대하는 모양({ stickerIds, stickers })은 그대로 둔다 - 고르개는 안 바뀐다.
// scope 로 한 번 더 거른다. 다른 채팅에서만 쓰는 콘을 즐겨찾기에 담아 뒀어도 여기서는 안 보인다.
// 저장된 ID 자체는 지우지 않는다 - 반대쪽 채팅에서는 여전히 쓸 수 있기 때문이다.
export async function loadStickerFavorites(scope) {
  const ids = readFavoriteIds();
  if (!ids.length) return { ok: true, stickerIds: [], stickers: [] };
  let unavailable = false;
  const found = await Promise.all(ids.map(async (id) => {
    try {
      const result = await loadStickers(`/v1/stickers/${encodeURIComponent(id)}?scope=${encodeURIComponent(scope || 'control')}`);
      return result?.sticker || null;
    } catch (error) {
      if (error.status !== 404) unavailable = true;
      return null;
    }
  }));
  const stickers = found.filter(Boolean);
  // 저장 여부는 로컬 ID가 결정한다. 조회 실패나 다른 사용 범위가 저장 목록을 바꾸면 안 된다.
  return { ok: true, stickerIds: ids, stickers, unavailable };
}

export async function setStickerFavorite(stickerId, favorite) {
  const id = String(stickerId || '').trim();
  if (!id) return { ok: true, favorite: false };
  const ids = readFavoriteIds().filter((item) => item !== id);
  if (favorite && ids.length >= FAVORITES_LIMIT) {
    throw new Error(`즐겨찾기는 최대 ${FAVORITES_LIMIT}개까지 저장할 수 있습니다.`);
  }
  // 새로 담은 것이 앞에 온다. 고르개가 담은 순서대로 보여 준다.
  writeFavoriteIds(favorite ? [id, ...ids] : ids);
  return { ok: true, favorite: Boolean(favorite) };
}
